import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, throwError } from 'rxjs';
import { AppErrorService } from '../services/app-error.service';

/** CAPS API domain patterns — only intercept errors from these hosts */
const CAPS_API_PATTERNS = ['api.columbia.edu', 'execute-api.us-east-1.amazonaws.com', 'localhost:8000'];

/**
 * Global HTTP interceptor for CAPS API 401/402 error handling.
 *
 * - 401 (no_user_found): Full-page error — user is authenticated but not provisioned in CAPS.
 * - 402 (insufficient_perms): Warning snackbar — user lacks permission for the specific endpoint.
 *
 * Errors always propagate to component-level handlers so loading states and inline messages still work.
 */
export const apiErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const appErrorService = inject(AppErrorService);
  const snackBar = inject(MatSnackBar);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only handle errors from CAPS API domains
      if (!isCapsApiUrl(req.url)) {
        return throwError(() => error);
      }

      if (error.status === 401) {
        handleNotProvisioned(error, appErrorService);
      } else if (error.status === 402) {
        handleInsufficientPermissions(error, snackBar);
      }

      // Always re-throw so caps.service.ts handleApiError() and component handlers still fire
      return throwError(() => error);
    })
  );
};

/** Check if the request URL belongs to a CAPS API domain */
function isCapsApiUrl(url: string): boolean {
  return CAPS_API_PATTERNS.some(pattern => url.includes(pattern));
}

/** Check if a 401 is a token-expiry issue (handled by AuthService) vs a not-provisioned issue */
function isTokenExpiry(error: HttpErrorResponse): boolean {
  // Support both legacy error_type and JSON:API standard errors[0].code
  const errorType = error.error?.error_type || error.error?.errors?.[0]?.code;
  const detail = error.error?.detail || error.error?.errors?.[0]?.detail || '';

  return (
    errorType === 'invalid_token' ||
    detail.toLowerCase().includes('token') ||
    detail.toLowerCase().includes('expired')
  );
}

/** 401: Set app-level error to show full-page not-provisioned screen */
function handleNotProvisioned(error: HttpErrorResponse, appErrorService: AppErrorService): void {
  // Skip if this is actually a token expiry — AuthService handles those
  if (isTokenExpiry(error)) {
    return;
  }

  appErrorService.setNotProvisionedError();
}

/** 402: Show warning snackbar with generic permission error message */
function handleInsufficientPermissions(error: HttpErrorResponse, snackBar: MatSnackBar): void {
  snackBar.open('You do not have permission to perform this action.', 'Dismiss', {
    duration: 6000,
    panelClass: ['warning-snackbar'],
    horizontalPosition: 'center',
    verticalPosition: 'top',
  });
}
